import { api } from "@/lib/api/client"
import type { BaseResponse, IdOnly } from "@/types/api"

import type {
  ConfirmArrivalPayload,
  DecisionPayload,
  IntakeLoad,
  LoadStatus,
  PendingArrival,
  SaveLabPayload,
  SaveSamplingPayload,
  SaveScalePayload,
} from "./types"

const BASE = "/exporter/intake"

export async function listPendingArrivals(): Promise<PendingArrival[]> {
  const { data } = await api.get<BaseResponse<PendingArrival[]>>(
    `${BASE}/pending`,
  )
  if (!data.success) {
    throw new Error(data.message ?? "Could not load pending arrivals")
  }
  return data.result ?? []
}

export async function listLoads(status?: LoadStatus): Promise<IntakeLoad[]> {
  const { data } = await api.get<BaseResponse<IntakeLoad[]>>(`${BASE}/loads`, {
    params: status ? { status } : undefined,
  })
  if (!data.success) {
    throw new Error(data.message ?? "Could not load intake loads")
  }
  return data.result ?? []
}

export async function getLoad(loadId: string | number): Promise<IntakeLoad> {
  const { data } = await api.get<BaseResponse<IntakeLoad>>(
    `${BASE}/loads/${loadId}`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not load intake load")
  }
  return data.result
}

export async function confirmArrival(
  payload: ConfirmArrivalPayload,
): Promise<IdOnly> {
  const { data } = await api.post<BaseResponse<IdOnly>>(
    `${BASE}/loads`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not confirm arrival")
  }
  return data.result
}

export async function saveScale(
  loadId: string | number,
  payload: SaveScalePayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/loads/${loadId}/scale`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not save scale")
  }
  return data.result
}

export async function saveSampling(
  loadId: string | number,
  payload: SaveSamplingPayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/loads/${loadId}/sampling`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not save sampling")
  }
  return data.result
}

export async function saveLab(
  loadId: string | number,
  payload: SaveLabPayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/loads/${loadId}/lab`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not save lab analysis")
  }
  return data.result
}

export async function decideLoad(
  loadId: string | number,
  payload: DecisionPayload,
): Promise<IdOnly> {
  const { data } = await api.post<BaseResponse<IdOnly>>(
    `${BASE}/loads/${loadId}/decision`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not record decision")
  }
  return data.result
}
